/**
 * What the three work views share: Oppgaver, Sjekklister and Årshjul.  MKK adoption Batch 1.
 *
 * The status and priority words, a due day in words (never a colour alone), the people work can be handed to, the
 * events a task or a checklist can be linked to, and a narrow-screen layout where the list and the detail are one
 * view at a time (a drawer beside the list on a wide screen).
 */
import React, { useEffect, useState } from 'react';
import { call, can, type Body } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { shortDate, wallClock } from '../lib/format';
import { Btn, Tag, type TagTone } from '../components/ui';
import { STATUS_WORDS, withTones } from '../core';

// The words are core's (tasks.STATUS_NO); the colour is the panel's.
export const STATUS: Record<string, [string, TagTone]> = withTones<TagTone>(STATUS_WORDS.task, { open: 'grey', in_progress: 'gold', done: 'green', cancelled: 'grey' }, 'grey');
export const PRIORITY: Record<string, [string, TagTone]> = { high: ['Høy', 'red'], normal: ['Normal', 'grey'], low: ['Lav', 'grey'] };

export const StatusTag = ({ status }: { status: string }) => {
  const [label, tone] = STATUS[status] || [status, 'grey'];
  return <Tag tone={tone}>{label}</Tag>;
};

/** A due day in words, with its state: "Forfalt · on 1. okt", "I dag", "fr 9. okt". */
export function DueText({ dueOn, due }: { dueOn: string | null; due?: string }) {
  if (!dueOn) return null;
  const when = <time dateTime={dueOn}>{shortDate(dueOn)}</time>;
  if (due === 'overdue') return <span className="work-due late">Forfalt · {when}</span>;
  if (due === 'today') return <span className="work-due today">I dag</span>;
  return <span className="work-due">Frist {when}</span>;
}

/** The people work can be handed to (GET /work/staff: active staff with work.assigned, id and name). */
export function useAssignable(enabled: boolean) {
  const { data } = useLoad<Body>(enabled ? '/work/staff' : null);
  return ((data && data.items) || []) as { id: string; name: string }[];
}

/** Upcoming and recent events, for linking — only for someone who can read the event list (events.write). */
export function useEvents() {
  const allowed = can('events.write');
  const { data } = useLoad<Body>(allowed ? '/events' : null);
  const items = ((data && data.items) || []) as Body[];
  return { allowed, items: items.filter((e) => e.status !== 'archived').slice(0, 100) };
}

/**
 * A linked event in words. Someone without events.write gets { linked: true } from the server — no id, no title — and is
 * told only that there is one (Batch 1 hardening): a draft event's title may name somebody.
 */
export function eventText(e: Body | null | undefined): string | null {
  if (!e) return null;
  if (e.linked) return 'Knyttet til et arrangement';
  return e.title || 'Arrangementet finnes ikke lenger';
}

export function EventSelect({ id, value, onChange, events, describedBy }: {
  id: string; value: string; onChange: (v: string) => void; events: Body[]; describedBy?: string;
}) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={describedBy}>
      <option value="">Ikke koblet til et arrangement</option>
      {events.map((e) => <option key={e.id} value={e.id}>{e.title_no} · {wallClock(e.starts_at).slice(0, 10)}</option>)}
    </select>
  );
}

export function PersonSelect({ id, value, onChange, people, none = 'Ingen ansvarlig', describedBy }: {
  id: string; value: string; onChange: (v: string) => void; people: { id: string; name: string }[]; none?: string; describedBy?: string;
}) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={describedBy}>
      <option value="">{none}</option>
      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
}

/** "3 av 7" with a progress bar that names itself. */
/** `mine` (Batch 1 remediation): the server counted only the viewer's own items (progress.scope 'mine'), and says so. */
export function Progress({ done, total, label, mine = false }: { done: number; total: number; label: string; mine?: boolean }) {
  return (
    <span className="work-progress">
      <progress max={total || 1} value={done} aria-label={`${label}: ${done} av ${total} ${mine ? 'av dine punkter' : 'punkter'} ferdig`} />
      <span aria-hidden="true">{done} av {total}{mine ? ' (dine)' : ''}</span>
    </span>
  );
}

/**
 * A list that comes a page at a time (Batch 1 hardening): the first page through useLoad, the next ones on «Vis flere»
 * (the server's nextOffset). A reload — after a change, or a new filter — starts again from the first page.
 */
export function usePaged(path: string | null, pick: (d: Body) => Body = (d) => d) {
  // `pick` (Batch 2A): where the page object sits in the answer — the top for the work hub, `registrations` for an
  // event's people (the same answer also carries the event and its counts).
  const first = useLoad<Body>(path);
  const page = first.data ? pick(first.data) : null;
  const [extra, setExtra] = useState<Body[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [moreError, setMoreError] = useState('');
  useEffect(() => { setExtra([]); setMoreError(''); setNext(page && page.nextOffset != null ? page.nextOffset : null); }, [first.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const more = async () => {
    if (next == null || !path) return;
    setBusy(true); setMoreError('');
    try {
      const d = pick(await call<Body>(`${path}${path.includes('?') ? '&' : '?'}offset=${next}`));
      setExtra((x) => [...x, ...((d.items || []) as Body[])]);
      setNext(d.nextOffset != null ? d.nextOffset : null);
    } catch (e) { setMoreError((e as Error).message); } finally { setBusy(false); }
  };
  const items = [...(((page && page.items) || []) as Body[]), ...extra];
  return { ...first, items, total: page ? Number(page.total) || 0 : 0, more: next != null ? more : null, busy, moreError };
}

/** «Vis flere (50 av 230)», and what went wrong if the next page did not come. */
export function MoreButton({ list }: { list: ReturnType<typeof usePaged> }) {
  if (!list.more && !list.moreError) return null;
  return (
    <div className="card-body work-more">
      {list.more && <Btn ghost disabled={list.busy} onClick={() => void list.more!()}>{list.busy ? 'Henter …' : `Vis flere (${list.items.length} av ${list.total})`}</Btn>}
      {list.moreError && <p className="msg err" role="alert">Fikk ikke hentet flere: {list.moreError}</p>}
    </div>
  );
}

/**
 * Close a detail: «Tilbake til listen» on a phone (where the detail is the whole view), «Lukk» beside the list on a wide
 * screen — the same button, always there (it was hidden at 900 px and up, so the drawer had no close control at all).
 */
export function BackButton({ onClick, what }: { onClick: () => void; what: string }) {
  return (
    <Btn ghost className="btn sm ghost work-back" onClick={onClick} aria-label={`Lukk ${what} og gå tilbake til listen`}>
      <span className="work-back-narrow">Tilbake til listen</span><span className="work-back-wide">Lukk</span>
    </Btn>
  );
}

/**
 * Escape closes the detail the focus is in — but not from inside a field (Escape there belongs to the field), and not
 * while something in it is unsaved (`blocked`): a key press must never throw away somebody's typing.
 */
export function useEscapeToClose(anchor: React.RefObject<HTMLElement>, onClose: () => void, blocked: boolean) {
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || blocked) return;
      const box = anchor.current ? anchor.current.closest('.work-detail') : null;
      const t = e.target as HTMLElement | null;
      if (!box || !t || !box.contains(t) || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, [anchor, onClose, blocked]);
}

/**
 * Somebody else saved first. The person's own edits are KEPT (they were thrown away before: the form was rebuilt from
 * the newer version), and they choose, in words: save theirs over the newer one, or drop theirs and see it.
 */
export function Conflict({ what, onKeepMine, onTakeTheirs, busy }: { what: string; onKeepMine: () => void; onTakeTheirs: () => void; busy?: boolean }) {
  return (
    <div className="msg warn work-conflict" role="alert">
      <p><b>Noen andre endret {what} mens du skrev.</b> Endringene dine er ikke lagret ennå, men de er beholdt her.</p>
      <div className="row">
        <Btn disabled={busy} onClick={onKeepMine}>Lagre mine endringer over de nye</Btn>
        <Btn ghost disabled={busy} onClick={onTakeTheirs}>Forkast mine og vis den nyeste</Btn>
      </div>
    </div>
  );
}

export { hashQuery } from '../lib/hash';
